import { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import Pagination from "./pagination";

function formatRelativeDate(dateString) {
  if (!dateString) return "";
  const now = new Date();
  const created = new Date(dateString);
  const diffMs = now - created;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMonths >= 3) return created.toLocaleDateString();
  if (diffMonths >= 1) return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
  if (diffWeeks >= 1) return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
  if (diffDays >= 1) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  return "Today";
}

export function ReviewList({
  reviews = [],
  emptyState = null,
  initialRowsPerPage = 4,
  rowsPerPageOptions = [4, 8, 12],
  rowLabel = "review items",
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    setCurrentPage((prev) => {
      const maxPage = Math.max(1, Math.ceil(Math.max(reviews.length, 1) / rowsPerPage));
      return Math.min(prev, maxPage);
    });
  }, [reviews.length, rowsPerPage]);

  if (!reviews.length) {
    return emptyState ?? null;
  }

  const totalPages = Math.max(1, Math.ceil(reviews.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const visibleReviews = reviews.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {visibleReviews.map((review) => {
          const reviewKey =
          review.id ??
            review._id ??
            review.created_at ??
            `${review.firstName ?? "review"}-${review.lastName ?? "anonymous"}`;
          return <ReviewCard key={reviewKey} review={review} />;
        })}
      </div>

         <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setCurrentPage(1);
        }}
        onPageChange={(page) =>
          setCurrentPage(Math.max(1, Math.min(page, totalPages)))
        }
        totalRows={reviews.length}
        rowLabel={rowLabel}
        showSelectedCount={false}
      />
    </div>
  );
}

export default function ReviewCard({ review = [], emptyState = null, initialRowsPerPage = 4, rowsPerPageOptions = [4, 8, 12], rowLabel = "review items" }) {
  if (!review) return null;

  const reviewerName = `${review.firstName ?? ""} ${review.lastName ?? ""}`.trim();


  return (
    <div className="flex flex-col sm:flex-row gap-4 p-4 border-b border-(--neutral-300) transition-colors">
      <img
        src={review.avatar_url}
        alt={reviewerName || "Reviewer avatar"}
        className="w-10 h-10 rounded-full object-cover shrink-0 bg-(--neutral-200)"
      />
      <div className="flex-1">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <FaStar
                key={i}
                className={`w-4 h-4 ${i < review.rating ? "fill-(--color-gold) text-(--color-gold)" : "text-(--neutral-300)"}`}
              />
            ))}
          </div>
          <span className="text-sm font-body text-(--neutral-500)]">
            {formatRelativeDate(review.created_at)}
            {reviewerName && ` by ${reviewerName}`}
          </span>
        </div>
        <p className="text-(--neutral-700) text-sm leading-relaxed font-body">
          {review.review}
        </p>
      </div>
    </div>
  );
}